import { useEffect, useState, useCallback } from "react";
import { api, weekImageUrl, formatApiErrorDetail } from "@/lib/apiClient";
import { AdminTabs } from "@/components/AdminTabs";
import { toast } from "sonner";
import { UploadCloud, Trash2, Plus } from "lucide-react";

export default function Admin() {
  const [weeks, setWeeks] = useState([]);
  const [title, setTitle] = useState("");
  const [weekNumber, setWeekNumber] = useState("");
  const [pageLabel, setPageLabel] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/weeks");
    setWeeks(data);
    if (!weekNumber && data.length === 0) setWeekNumber("1");
    else if (!weekNumber) setWeekNumber(String(data[0].week_number + 1));
  }, [weekNumber]);

  useEffect(() => {
    load().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    if (f) setPreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please select a scanned page image.");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("title", title);
      form.append("week_number", parseInt(weekNumber, 10));
      form.append("description", description);
      form.append("page_label", pageLabel);
      form.append("file", file);
      await api.post("/weeks", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Weekly page unsealed.");
      setTitle("");
      setPageLabel("");
      setDescription("");
      setFile(null);
      setPreview(null);
      setWeekNumber("");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/weeks/${id}`);
      setWeeks((w) => w.filter((x) => x.id !== id));
      toast.success("Page withdrawn from the archive.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-16">
      <AdminTabs />
      <div className="mb-12">
        <div className="font-mono text-xs tracking-[0.3em] uppercase text-oxblood mb-4">
          Archivist's Desk
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight leading-none">Unseal a Weekly Page</h1>
        <p className="font-body text-text-muted mt-5 max-w-2xl leading-relaxed">
          Upload a scanned leaf of the 1872 volume. Once published, members can read it and add
          their remarks.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-12">
        <form onSubmit={submit} className="bg-surface border border-archive-border offset-shadow p-8 space-y-6">
          <div className="flex items-center gap-2 font-serif text-2xl text-ink">
            <Plus strokeWidth={1.5} className="w-5 h-5 text-oxblood" /> New Page
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block font-mono text-xs tracking-widest uppercase text-text-muted mb-2">
                Week #
              </label>
              <input
                type="number"
                min="1"
                required
                value={weekNumber}
                onChange={(e) => setWeekNumber(e.target.value)}
                data-testid="admin-week-number"
                className="w-full bg-parchment border border-archive-border px-4 py-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-oxblood"
              />
            </div>
            <div>
              <label className="block font-mono text-xs tracking-widest uppercase text-text-muted mb-2">
                Page Label
              </label>
              <input
                type="text"
                value={pageLabel}
                onChange={(e) => setPageLabel(e.target.value)}
                data-testid="admin-page-label"
                placeholder="e.g. pp. 12–13"
                className="w-full bg-parchment border border-archive-border px-4 py-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-oxblood"
              />
            </div>
          </div>

          <div>
            <label className="block font-mono text-xs tracking-widest uppercase text-text-muted mb-2">
              Title
            </label>
            <input
              type="text"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="admin-title"
              placeholder="A short title for this page"
              className="w-full bg-parchment border border-archive-border px-4 py-3 font-body text-ink focus:outline-none focus:ring-2 focus:ring-oxblood"
            />
          </div>

          <div>
            <label className="block font-mono text-xs tracking-widest uppercase text-text-muted mb-2">
              Notes / Context
            </label>
            <textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              data-testid="admin-description"
              placeholder="Optional context for the discussion"
              className="w-full bg-parchment border border-archive-border px-4 py-3 font-body text-ink resize-none focus:outline-none focus:ring-2 focus:ring-oxblood"
            />
          </div>

          <div>
            <label className="block font-mono text-xs tracking-widest uppercase text-text-muted mb-2">
              Scanned Page Image
            </label>
            <label className="flex flex-col items-center justify-center gap-3 border border-dashed border-archive-border bg-parchment px-4 py-8 cursor-pointer hover:border-oxblood transition-colors">
              {preview ? (
                <img src={preview} alt="preview" className="max-h-48 object-contain" />
              ) : (
                <>
                  <UploadCloud strokeWidth={1.5} className="w-8 h-8 text-oxblood" />
                  <span className="font-mono text-xs tracking-widest uppercase text-text-muted">
                    Choose an image (jpg, png)
                  </span>
                </>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={onFile}
                data-testid="admin-file-input"
                className="hidden"
              />
            </label>
            {file && (
              <div className="font-mono text-[10px] tracking-widest uppercase text-text-muted mt-2">
                {file.name}
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={busy}
            data-testid="admin-publish-button"
            className="w-full bg-oxblood text-parchment py-4 font-mono text-xs tracking-widest uppercase hover:bg-oxblood-dark transition-colors disabled:opacity-60"
          >
            {busy ? "Publishing…" : "Publish Weekly Page"}
          </button>
        </form>

        <div>
          <h2 className="font-serif text-2xl text-ink mb-6">Published Pages</h2>
          {weeks.length === 0 ? (
            <p className="font-body italic text-text-muted">No pages published yet.</p>
          ) : (
            <div className="space-y-4">
              {weeks.map((w) => (
                <div
                  key={w.id}
                  data-testid={`admin-week-row-${w.week_number}`}
                  className="flex items-center gap-4 bg-surface border border-archive-border p-4"
                >
                  <img
                    src={weekImageUrl(w.id)}
                    alt={w.title}
                    className="w-16 h-20 object-cover border border-archive-border shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-[10px] tracking-widest uppercase text-oxblood">
                      Week {String(w.week_number).padStart(2, "0")}
                    </div>
                    <div className="font-serif text-lg text-ink truncate">{w.title}</div>
                    <div className="font-mono text-[10px] tracking-widest uppercase text-text-muted mt-1">
                      {w.comment_count} remarks
                    </div>
                  </div>
                  <button
                    onClick={() => remove(w.id)}
                    data-testid={`admin-delete-${w.week_number}`}
                    className="text-text-muted hover:text-oxblood transition-colors shrink-0"
                    title="Withdraw page"
                  >
                    <Trash2 strokeWidth={1.5} className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
